import { FormInstance } from 'antd';
import { AxiosError, AxiosRequestConfig, CancelToken, Method } from 'axios';
import { defaultsDeep } from 'lodash';
import { useCallback, useState } from 'react';
import axios from './axios';
import { basicSuccessHandler, basicErrorHandler, setFormErrors } from './handlers';

type APIObject = {
  method: Method;
  url: (input: any) => string;
  cacheable?: boolean | number;
  debug?: boolean;
} & Partial<Omit<import('axios').AxiosRequestConfig, 'url' | 'method' | 'params'>>;

export type AxiosOpt = AxiosRequestConfig & {
  cancelToken?: CancelToken;
  notification?: boolean | string;
  successNotification?: boolean | string;
  errorNotification?: boolean | string;
  onError?: (err: AxiosError) => void;
  form?: FormInstance;
};
export type Variables = PlainObject<any>;
export type ExecuteFn = (data?: Variables) => Promise<any>;
type UseAPIResultState = { loading: boolean; data: any; error: any | null };
export type UseAPI = (axiosOptions?: AxiosOpt) => [ExecuteFn, UseAPIResultState];

function getApiHook(apiObject: APIObject): UseAPI {
  return (axiosOptions: AxiosOpt = {}) => {
    const [state, setState] = useState<UseAPIResultState>({
      loading: false,
      data: null,
      error: null,
    });

    const {
      successNotification = axiosOptions.notification,
      errorNotification = axiosOptions.notification,
      form,
      onError,
      cancelToken,
    } = axiosOptions ?? {};

    const executeFn = useCallback(
      async (data: Variables = {}) => {
        const { cacheable, url, method, debug, ...rest } = apiObject;
        if (data.params) {
          data.params = defaultsDeep(data.params || {}, data.params);
        }
        const { input } = data;
        const finalOpts: AxiosRequestConfig = {
          ...rest,
          ...data,
          url: url(input),
          method,
        };
        if (cancelToken) {
          finalOpts.cancelToken = cancelToken;
        }
        try {
          setState((prevState) => ({ ...prevState, loading: true }));
          const response = await axios(finalOpts);
          setState({ loading: false, data: response.data, error: null });
          if (successNotification) {
            basicSuccessHandler(response, { notification: successNotification });
          }
          return response.data;
        }
        catch (err: any) {
          setState({ loading: false, data: null, error: err });
          if (onError) {
            onError(err);
          }
          if (form) {
            setFormErrors(err, form);
          }
          if (errorNotification) {
            basicErrorHandler(err, { notification: errorNotification });
          }
          // TODO: Could turn this off with an option for calls that don't require an error state
          // Rethrow to consume by API callers in Components
          throw err;
        }
      },
      [cancelToken, errorNotification, form, onError, successNotification],
    );
    return [executeFn, state];
  };
}

const useApi = {
  Auth: {
    Login: getApiHook({
      method: 'POST',
      url: () => '/auth/login',
    }),
    Logout: getApiHook({
      method: 'GET',
      url: () => '/auth/logout',
    }),
    ForgotPassword: getApiHook({
      method: 'POST',
      url: () => '/auth/forgot',
    }),
    ValidateResetToken: getApiHook({
      method: 'GET',
      url: ({ token }: { token: string }) => `/auth/reset-password/${token}`,
    }),
    ResetPassword: getApiHook({
      method: 'POST',
      url: ({ token }: { token: string }) => `/auth/reset-password/${token}`,
    }),
    VirtualLogin: getApiHook({
      method: 'POST',
      url: () => '/auth/virtual-login',
    }),
    VirtualLogout: getApiHook({
      method: 'GET',
      url: () => '/auth/virtual-logout',
    }),
  },
  Advertiser: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/advertisers/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/advertisers',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/advertisers/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/advertisers',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/advertisers/${id}`,
    }),
    UpdateAppsFlyerToken: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/advertisers/${id}/appsflyer-token`,
    }),
    GetAppsFlyerTokenStatus: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/advertisers/${id}/appsflyer-token`,
    }),
    DeleteAppsFlyerToken: getApiHook({
      method: 'DELETE',
      url: ({ id }: { id: string }) => `/advertisers/${id}/appsflyer-token`,
    }),
    PerformanceConsole: getApiHook({
      method: 'GET',
      url: () => '/advertisers/performance-console',
    }),
    ListEvents: getApiHook({
      method: 'GET',
      url: () => `/advertisers/events`,
    }),
  },
  Exchange: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/exchanges/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/exchanges',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/exchanges/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/exchanges',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/exchanges/${id}`,
    }),
  },
  RateLimit: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/rate-limit/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/rate-limit',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/rate-limit',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/rate-limit/${id}`,
    }),
    Delete: getApiHook({
      method: 'DELETE',
      url: ({ id }: { id: string }) => `/rate-limit/${id}`,
    }),
  },
  BidRule: {
    Meta: getApiHook({
      method: 'GET',
      url: () => '/bid-rule/meta',
      cacheable: true,
    }),
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/bid-rule/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/bid-rule',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/bid-rule',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/bid-rule/${id}`,
    }),
    Delete: getApiHook({
      method: 'DELETE',
      url: ({ id }: { id: string }) => `/bid-rule/${id}`,
    }),
  },
  AppAsset: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/app-assets/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/app-assets',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/app-assets/search',
    }),
    Package: getApiHook({
      method: 'GET',
      url: () => `/app-assets/package`,
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/app-assets',
    }),
  },
  AppList: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/app-lists/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/app-lists',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/app-lists/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/app-lists',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/app-lists/${id}`,
    }),
  },
  Audience: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/audiences/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/audiences',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/audiences/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/audiences',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/audiences/${id}`,
    }),
  },
  CustomAudience: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/audiences/${id}`,
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/audiences',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/customAudiences/${id}`,
    }),
  },
  Campaign: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/campaigns/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/campaigns',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/campaigns/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/campaigns',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/campaigns/${id}`,
    }),
    PreviewCreatives: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/campaigns/${id}/preview/creatives`,
    }),
    Preview: getApiHook({
      method: 'POST',
      url: ({ id }: { id: string }) => `/campaigns/${id}/preview`,
    }),
    PreviewReceipts: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/campaigns/${id}/preview/receipts`,
    }),
  },
  Creative: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/creatives/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/creatives',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/creatives',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/creatives/search',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/creatives/${id}`,
    }),
  },
  Dashboard: {
    Get: getApiHook({
      method: 'GET',
      url: () => `/dashboard`,
    }),
    Update: getApiHook({
      method: 'POST',
      url: ({ id }: { id: string }) => `/dashboard/${id}`,
    }),
  },
  File: {
    GetAppReport: getApiHook({
      method: 'GET',
      url: () => `/file/exported`,
    }),
    Upload: getApiHook({
      method: 'POST',
      url: () => '/file/upload',
    }),
    UploadFilePart: getApiHook({
      method: 'POST',
      url: () => '/file/multi-upload',
    }),
    CancelMultiUpload: getApiHook({
      method: 'POST',
      url: () => '/file/cancel',
    }),
  },
  Fund: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/funds/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/funds',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/funds',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/funds/${id}`,
    }),
  },
  Geo: {
    List: getApiHook({
      method: 'GET',
      url: () => '/locations',
    }),
  },
  IpList: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/ip-list/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/ip-list',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/ip-list/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/ip-list',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/ip-list/${id}`,
    }),
  },
  Order: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/orders/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/orders',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/orders',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/orders/search',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/orders/${id}`,
    }),
  },
  Stats: {
    Total: getApiHook({
      method: 'GET',
      url: () => `/stats`,
    }),
    Custom: getApiHook({
      method: 'GET',
      url: () => `/stats/custom`,
    }),
    TagId: getApiHook({
      method: 'GET',
      url: () => `/stats/custom/tagid`,
    }),
  },
  Tracker: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/trackers/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/trackers',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/trackers/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/trackers',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/trackers/${id}`,
    }),
  },
  Inventory: {
    Get: getApiHook({
      method: 'GET',
      url: () => `/inventory`,
    }),
  },
  Inventory2: {
    Get: getApiHook({
      method: 'GET',
      url: () => `/inventory/bids-wins`,
    }),
  },
  User: {
    Get: getApiHook({
      method: 'GET',
      url: ({ id }: { id: string }) => `/users/${id}`,
    }),
    List: getApiHook({
      method: 'GET',
      url: () => '/users',
    }),
    Search: getApiHook({
      method: 'GET',
      url: () => '/users/search',
    }),
    Create: getApiHook({
      method: 'POST',
      url: () => '/users',
    }),
    Update: getApiHook({
      method: 'PUT',
      url: ({ id }: { id: string }) => `/users/${id}`,
    }),
  },
};

export default useApi;
